/** WebRTC mesh call manager: polls for incoming calls (incoming overlay),
 *  hosts the active-call overlay (remote tiles + self PiP + controls), and
 *  runs perfect-negotiation signaling over REST polling. Mounted once in
 *  Dashboard so calls can arrive anywhere in the app. */
import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, MicOff, Phone, PhoneOff, Video, VideoOff } from "lucide-react";
import { toast } from "sonner";
import Avatar from "./Avatar";
import {
  acceptCall, declineCall, fetchActiveCalls, leaveCall,
  pollSignals, rtcConfig, sendSignal, startCall,
} from "./callApi";

function CallTile({ stream, name, picture, audioOnly, mirror, muted, badge }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && stream) ref.current.srcObject = stream;
  }, [stream]);
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-black/60" data-testid={`call-tile-${name}`}>
      {/* the element must exist (hidden) on audio calls so sound plays */}
      {stream ? (
        <video ref={ref} autoPlay playsInline muted={muted}
          className={audioOnly ? "hidden" : `h-full w-full object-cover ${mirror ? "-scale-x-100" : ""}`} />
      ) : null}
      {(!stream || audioOnly) && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Avatar name={name} picture={picture} showStatus={false} size={72} />
        </div>
      )}
      <span className="absolute bottom-2 left-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-white">
        {name}{badge ? ` · ${badge}` : ""}
      </span>
    </div>
  );
}

// conference grid: 1 → full-width, 2 → stacked on phones / side-by-side on
// desktop, 3-4 → 2 cols, 5+ → 3 cols
const GRID_COLS = { 1: "grid-cols-1", 2: "grid-cols-1 sm:grid-cols-2", 3: "grid-cols-2", 4: "grid-cols-2" };

export default function CallManager({ me }) {
  const [incoming, setIncoming] = useState(null);   // ringing call addressed at me
  const [active, setActive] = useState(null);       // call doc I'm in
  const [roster, setRoster] = useState([]);         // participant states
  const [streams, setStreams] = useState({});       // peerId -> MediaStream
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);

  const localStreamRef = useRef(null);
  const peersRef = useRef(new Map());               // peerId -> { pc, makingOffer }
  const iceRef = useRef(null);
  const seqRef = useRef(0);
  const activeRef = useRef(null);
  activeRef.current = active;

  /* ---------- signaling primitives ---------- */
  const closePeer = useCallback((peerId) => {
    const p = peersRef.current.get(peerId);
    if (p) { try { p.pc.close(); } catch { /* noop */ } peersRef.current.delete(peerId); }
    setStreams((s) => { const n = { ...s }; delete n[peerId]; return n; });
  }, []);

  /* ---------- ringtone / ringback ----------
   * WebAudio loops (no assets). Plays through the device's current audio
   * route — speaker normally, headset when one is connected. Browsers gate
   * audio behind a user gesture; if blocked we just stay silent (the
   * overlay + vibration still alert). */
  const ringCtxRef = useRef(null);
  const ringTimerRef = useRef(null);
  const ringKindRef = useRef(null);

  const stopRing = useCallback(() => {
    ringKindRef.current = null;
    if (ringTimerRef.current) { clearInterval(ringTimerRef.current); ringTimerRef.current = null; }
    if (ringCtxRef.current) { try { ringCtxRef.current.close(); } catch { /* noop */ } ringCtxRef.current = null; }
  }, []);

  const startRing = useCallback((kind) => {
    if (ringKindRef.current === kind) return; // don't restart an identical ring mid-cycle
    stopRing();
    ringKindRef.current = kind;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      ringCtxRef.current = ctx;
      const tone = (freqs, dur) => {
        freqs.forEach((f) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = "sine";
          o.frequency.value = f;
          const t0 = ctx.currentTime;
          g.gain.setValueAtTime(0.0001, t0);
          g.gain.linearRampToValueAtTime(0.22, t0 + 0.05);
          g.gain.setValueAtTime(0.22, t0 + dur - 0.08);
          g.gain.linearRampToValueAtTime(0.0001, t0 + dur);
          o.connect(g);
          g.connect(ctx.destination);
          o.start(t0);
          o.stop(t0 + dur + 0.05);
        });
      };
      let cycle;
      if (kind === "incoming") {
        // classic phone ring: 1.4s dual-tone burst every 3s + buzz
        cycle = () => {
          tone([850, 620], 1.4);
          try { navigator.vibrate?.([600, 300, 600]); } catch { /* unsupported */ }
        };
        cycle();
        ringTimerRef.current = setInterval(cycle, 3000);
      } else {
        // outgoing ringback: soft 440+480 beep, 1.2s every 3.5s
        cycle = () => tone([440, 480], 1.2);
        cycle();
        ringTimerRef.current = setInterval(cycle, 3500);
      }
    } catch { /* audio blocked until a user gesture */ }
  }, [stopRing]);

  // ring driver: incoming ringtone while the popup is up; ringback for the
  // caller until somebody joins; silence otherwise
  const joinedCount = roster.filter((p) => p.state === "joined" && p.user_id !== me.user_id).length;
  useEffect(() => {
    if (incoming && !active) startRing("incoming");
    else if (active && active.initiator_id === me.user_id && joinedCount === 0) startRing("outgoing");
    else stopRing();
  }, [incoming, active, joinedCount, startRing, stopRing, me.user_id]);
  useEffect(() => stopRing, [stopRing]); // unmount cleanup

  const hangUp = useCallback(async () => {
    const call = activeRef.current;
    for (const pid of [...peersRef.current.keys()]) closePeer(pid);
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    setActive(null);
    setStreams({});
    setMuted(false);
    setCamOff(false);
    if (call) await leaveCall(call.call_id).catch(() => {});
  }, [closePeer]);

  const getPeer = useCallback((peerId) => {
    let entry = peersRef.current.get(peerId);
    if (entry) return entry;
    const pc = new RTCPeerConnection(iceRef.current);
    const call = activeRef.current;
    const polite = me.user_id > peerId; // deterministic: avoids offer glare
    entry = { pc, makingOffer: false, polite };
    localStreamRef.current?.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
    pc.onicecandidate = (e) => {
      if (e.candidate && call) sendSignal(call.call_id, peerId, "ice", e.candidate.toJSON()).catch(() => {});
    };
    pc.ontrack = (e) => {
      setStreams((s) => ({ ...s, [peerId]: e.streams[0] }));
    };
    pc.onconnectionstatechange = () => {
      if (["failed", "closed", "disconnected"].includes(pc.connectionState)) closePeer(peerId);
    };
    pc.onnegotiationneeded = async () => {
      try {
        entry.makingOffer = true;
        await pc.setLocalDescription();
        if (activeRef.current) {
          await sendSignal(activeRef.current.call_id, peerId, "offer", { type: "offer", sdp: pc.localDescription.sdp });
        }
      } catch { /* retried on next negotiation */ } finally {
        entry.makingOffer = false;
      }
    };
    peersRef.current.set(peerId, entry);
    return entry;
  }, [closePeer, me.user_id]);

  const handleSignal = useCallback(async (sig) => {
    const entry = getPeer(sig.from);
    const pc = entry.pc;
    try {
      if (sig.type === "offer") {
        const collision = entry.makingOffer || pc.signalingState !== "stable";
        if (!entry.polite && collision) return; // glare: impolite side backs off
        await pc.setRemoteDescription(sig.payload);
        await pc.setLocalDescription();
        await sendSignal(activeRef.current.call_id, sig.from, "answer", { type: "answer", sdp: pc.localDescription.sdp });
      } else if (sig.type === "answer") {
        await pc.setRemoteDescription(sig.payload);
      } else if (sig.type === "ice") {
        try { await pc.addIceCandidate(sig.payload); } catch { /* stale candidate */ }
      }
    } catch { /* peer vanished mid-handshake */ }
  }, [getPeer]);

  /* ---------- join a call ---------- */
  const join = useCallback(async (call) => {
    try {
      if (!iceRef.current) iceRef.current = await rtcConfig();
      const wantVideo = call.kind === "video";
      const stream = await navigator.mediaDevices.getUserMedia({
        video: wantVideo ? { facingMode: "user", width: { ideal: 1280 } } : false,
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      localStreamRef.current = stream;
      await acceptCall(call.call_id);
      seqRef.current = 0;
      setActive(call);
      setRoster(call.participants || []);
    } catch (e) {
      if (e?.name === "NotAllowedError" || e?.name === "NotFoundError") {
        toast.error("Camera/microphone blocked — allow access in the browser prompt");
      } else {
        toast.error("Could not join the call");
      }
    }
  }, []);

  /* ---------- polling: incoming calls ---------- */
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const calls = await fetchActiveCalls();
        if (stop) return;
        const meJoined = calls.find((c) => c.my_state === "joined");
        if (meJoined && !activeRef.current) {
          // joined on another device / refreshed mid-call — rejoin media
          join(meJoined);
          return;
        }
        const ring = calls.find((c) => c.my_state === "ringing" && c.initiator_id !== me.user_id);
        setIncoming(activeRef.current ? null : (ring || null));
      } catch { /* transient */ }
    };
    tick();
    const t = setInterval(tick, 5000);
    return () => { stop = true; clearInterval(t); };
  }, [join, me.user_id]);

  /* ---------- start-call requests from chat threads ---------- */
  useEffect(() => {
    const onStart = async (e) => {
      const { conversationId, kind, convoName } = e.detail || {};
      if (!conversationId) return;
      try {
        const call = await startCall(conversationId, kind);
        join({ ...call, convo_name: convoName || call.convo_name });
      } catch {
        toast.error("Could not start the call");
      }
    };
    window.addEventListener("em:start-call", onStart);
    return () => window.removeEventListener("em:start-call", onStart);
  }, [join]);

  /* ---------- in-call signaling loop ---------- */
  useEffect(() => {
    if (!active) return undefined;
    let stop = false;
    const loop = async () => {
      try {
        const data = await pollSignals(active.call_id, seqRef.current);
        if (stop) return;
        for (const s of data.signals) {
          seqRef.current = Math.max(seqRef.current, s.seq);
          await handleSignal(s);
        }
        setRoster(data.participants || []);
        if (data.status === "ended") { hangUp(); return; }
        // open links to freshly-joined participants (onnegotiationneeded fires)
        const joined = (data.participants || []).filter((p) => p.state === "joined" && p.user_id !== me.user_id);
        for (const p of joined) getPeer(p.user_id);
        // drop links for people who left
        for (const pid of [...peersRef.current.keys()]) {
          if (!joined.find((p) => p.user_id === pid)) closePeer(pid);
        }
      } catch { /* keep polling */ }
    };
    const t = setInterval(loop, 1000);
    loop();
    return () => { stop = true; clearInterval(t); };
  }, [active, getPeer, handleSignal, hangUp, me.user_id]);

  const toggleMute = () => {
    localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = muted; });
    setMuted(!muted);
  };
  const toggleCam = () => {
    localStreamRef.current?.getVideoTracks().forEach((t) => { t.enabled = camOff; });
    setCamOff(!camOff);
  };

  const peerMeta = (uid) => roster.find((p) => p.user_id === uid) || {};
  const joinedOthers = roster.filter((p) => p.state === "joined" && p.user_id !== me.user_id);
  // conference grid: one tile per person — you first, then everyone joined,
  // then anyone still ringing (avatar + "calling…" until they pick up)
  const ringingOthers = roster.filter((p) => p.state === "ringing" && p.user_id !== me.user_id);
  const tiles = [
    { uid: me.user_id, name: "You", self: true },
    ...joinedOthers.map((p) => ({ uid: p.user_id, name: peerMeta(p.user_id).name || "…" })),
    ...ringingOthers.map((p) => ({ uid: p.user_id, name: peerMeta(p.user_id).name || "…", badge: "calling…" })),
  ];
  const gridCols = GRID_COLS[tiles.length] || "grid-cols-2 sm:grid-cols-3";

  return (
    <>
      {/* incoming call */}
      {incoming && !active && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" data-testid="incoming-call">
          <div className="w-full max-w-xs rounded-2xl bg-white p-6 text-center shadow-2xl">
            <p className="mb-3 animate-pulse font-mono text-[10px] font-semibold uppercase tracking-[0.28em] text-[#1a3fd1]" data-testid="incoming-call-title">
              Incoming {incoming.kind === "video" ? "video" : "voice"} call
            </p>
            <div className="mx-auto mb-3 w-fit animate-pulse">
              <Avatar name={incoming.caller_name} picture={incoming.caller_picture} showStatus={false} size={72} />
            </div>
            <p className="text-base font-bold text-gray-900">{incoming.convo_name}</p>
            <p className="mb-5 text-xs text-gray-500">
              {incoming.caller_name}{incoming.is_group ? " · group call" : ""}
            </p>
            <div className="flex items-center justify-center gap-4">
              <button onClick={async () => { await declineCall(incoming.call_id).catch(() => {}); setIncoming(null); }}
                data-testid="call-decline-btn" aria-label="Ignore call"
                className="flex items-center gap-2 rounded-full bg-red-500 px-5 py-3 text-sm font-semibold text-white shadow-lg hover:bg-red-600">
                <PhoneOff className="h-4 w-4" />
                Ignore
              </button>
              <button onClick={() => { const c = incoming; setIncoming(null); join(c); }}
                data-testid="call-accept-btn" aria-label="Answer call"
                className="flex animate-bounce items-center gap-2 rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-white shadow-lg hover:bg-emerald-600">
                {incoming.kind === "video" ? <Video className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                Answer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* active call */}
      {active && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-[#0b0f19]" data-testid="call-overlay">
          <div className="flex shrink-0 items-center justify-between px-4 py-3">
            <p className="text-sm font-semibold text-white">
              {active.convo_name || "Call"}
              <span className="ml-2 text-[10px] font-normal uppercase tracking-widest text-white/50">
                {joinedOthers.length ? `${joinedOthers.length + 1} on call` : "ringing…"}
              </span>
            </p>
          </div>
          <div className={`grid min-h-0 flex-1 place-content-center gap-2 p-3 ${gridCols}`} data-testid="call-grid">
            {tiles.map((t) => (
              <CallTile
                key={t.uid}
                stream={t.self ? localStreamRef.current : streams[t.uid]}
                name={t.name}
                picture={t.self ? me.picture : peerMeta(t.uid).picture}
                audioOnly={active.kind === "audio" || (t.self && camOff)}
                mirror={t.self}
                muted={t.self}
                badge={t.badge}
              />
            ))}
          </div>
          {/* controls */}
          <div className="flex shrink-0 items-center justify-center gap-4 pb-8 pt-2">
            <button onClick={toggleMute} data-testid="call-mute-btn" aria-label="Mute microphone"
              className={`rounded-full p-3.5 shadow-lg ${muted ? "bg-white text-gray-900" : "bg-white/15 text-white hover:bg-white/25"}`}>
              {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>
            {active.kind === "video" && (
              <button onClick={toggleCam} data-testid="call-camera-btn" aria-label="Toggle camera"
                className={`rounded-full p-3.5 shadow-lg ${camOff ? "bg-white text-gray-900" : "bg-white/15 text-white hover:bg-white/25"}`}>
                {camOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
              </button>
            )}
            <button onClick={hangUp} data-testid="call-end-btn" aria-label="End call"
              className="rounded-full bg-red-500 p-4 text-white shadow-lg hover:bg-red-600">
              <PhoneOff className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
